import React from 'react';
import { processStyleDefinition, processStyleValueWithFunction } from '../../util/styleProcessor';
import { styleDefaults, styleProperties } from './TemplateEditorStyleProperties';
import { StyleResolution } from '../../types/common';

const PREFIX = '.comp.compTemplateEditor';
export default function TemplateEditorStyle({
	theme,
}: Readonly<{
	theme: Map<string, Map<string, string>>;
}>) {
	// Theme value for this component's own chrome, falling back to the literal it
	// replaced. The fallback is what makes this safe on a theme that predates the
	// variable: an absent value renders exactly as the hardcoded CSS did. Resolved
	// through processStyleValueWithFunction so a theme value that is itself a
	// `<var>` reference still resolves.
	const all = theme.get(StyleResolution.ALL) ?? new Map<string, string>();
	const t = (variable: string, fallback: string) =>
		all.get(variable) ? processStyleValueWithFunction(`<${variable}>`, all) : fallback;

	const has = (variable: string) => !!all.get(variable);
	// The first of `variables` the theme defines, else the literal.
	const tf = (variables: string[], fallback: string) => {
		const found = variables.find(has);
		return found ? t(found, fallback) : fallback;
	};

	// Roles. appbuilder's palettes carry an accent family: accentWashColor, a dark accent
	// for text in colorTwo and a blue selection in colorFive. Mono has none of it (colorTwo
	// is its canvas, colorFive a border), so a theme without accentWashColor takes ink for
	// those roles, as the mono guide does.
	const accentFamily = has('accentWashColor');
	const ink = t('fontColorOne', '#111827');
	const muted = t('fontColorThree', '#6b7280');
	const faint = tf(['fontColorTen', 'fontColorThree'], '#9ca3af');
	const card = t('colorSeven', '#fff');
	const panel = tf(['surfaceColorOne'], '#fbfbfd');
	const border = tf(['borderColorEleven', 'borderColorTwo', 'fontColorEight'], '#e0e0e0');
	// Lighter hairline for internal dividers, to keep the editor from looking busy.
	const line = tf(['hairlineColor', 'borderColorOne'], '#eef0f3');
	const wash = tf(['accentWashColor', 'accentTintColor'], '#eef2ff');
	const accent = accentFamily ? t('colorOne', '#6366f1') : ink;
	const accentText = accentFamily ? t('colorTwo', '#4338ca') : ink;
	const selection = accentFamily ? t('colorFive', '#3b82f6') : ink;
	const danger = tf(['errorColor', 'colorTwelve'], '#b91c1c');

	const css =
		`
	${PREFIX} {
		display: flex;
		flex-direction: column;
		min-height: 480px;
		height: 100%;
		flex: 1;
		color: ${ink};
	}

	/* Native controls take the theme's ink and card instead of the browser's black on white.
	   Zero specificity, so every class rule below still wins. */
	:where(${PREFIX}) :where(button, select, input, textarea) { color: inherit; font-family: inherit; }
	:where(${PREFIX}) :where(select, textarea, input:not([type=color]):not([type=checkbox]):not([type=radio])) {
		background-color: ${card};
	}

	${PREFIX} ._templateEditorLoading {
		padding: 20px;
	}

	${PREFIX} ._templateEditor {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		border-radius: 8px;
		overflow: hidden;
		background: ${card};
	}

	/* Toolbar */
	${PREFIX} ._teToolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		padding: 10px 14px;
		border-bottom: 1px solid ${line};
		flex-wrap: wrap;
	}
	${PREFIX} ._teToolbarLeft { display: flex; gap: 12px; align-items: center; }
	${PREFIX} ._teToolbarRight { display: flex; gap: 6px; align-items: center; }
	${PREFIX} ._teSelect {
		display: flex;
		flex-direction: column;
		font-size: 11px;
		gap: 3px;
		color: ${t('fontColorTwo', '#666')};
	}
	${PREFIX} ._teSelect select {
		padding: 5px 8px;
		border: 1px solid ${border};
		border-radius: 6px;
		font-size: 13px;
		min-width: 120px;
		background: ${card};
		color: ${ink};
	}
	${PREFIX} ._teTab {
		padding: 6px 12px;
		border: 1px solid transparent;
		border-radius: 6px;
		background: transparent;
		cursor: pointer;
		font-size: 13px;
		color: ${t('fontColorThree', '#6b7280')};
	}
	${PREFIX} ._teTab:hover { background: ${t('surfaceColorTwo', '#f3f4f6')}; }
	${PREFIX} ._teTab._active { background: ${wash}; color: ${accentText}; }

	/* Body: editor | preview | right panel */
	${PREFIX} ._teBody {
		display: flex;
		flex: 1;
		min-height: 0;
	}
	${PREFIX} ._teEditorColumn {
		display: flex;
		flex-direction: column;
		flex: 1 1 45%;
		min-width: 0;
		border-right: 1px solid ${line};
	}
	${PREFIX} ._tePreviewColumn {
		display: flex;
		flex-direction: column;
		flex: 1 1 45%;
		min-width: 0;
	}
	${PREFIX} ._teRightPanel {
		flex: 0 0 330px;
		border-left: 1px solid ${line};
		overflow: auto;
		padding: 16px;
	}

	/* Part tabs (underline style, no boxes) */
	${PREFIX} ._partTabs {
		display: flex;
		gap: 14px;
		padding: 8px 14px 0 14px;
	}
	${PREFIX} ._partTab {
		padding: 6px 2px;
		border: none;
		border-bottom: 2px solid transparent;
		background: transparent;
		cursor: pointer;
		font-size: 13px;
		color: ${t('fontColorThree', '#6b7280')};
	}
	${PREFIX} ._partTab._active { color: ${accentText}; border-bottom-color: ${accent}; font-weight: 600; }

	${PREFIX} ._partEditor {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
	}
	${PREFIX} ._partToolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 6px 8px;
		gap: 8px;
	}
	${PREFIX} ._partLabel { font-size: 12px; color: ${t('fontColorTwo', '#666')}; font-weight: 600; }
	${PREFIX} ._htmlEditor { flex: 1; min-height: 0; }
	${PREFIX} ._textPart {
		flex: 1;
		margin: 0 8px 8px 8px;
		padding: 8px;
		border: 1px solid ${border};
		border-radius: 4px;
		font-family: inherit;
		font-size: 13px;
		resize: none;
	}
	${PREFIX} ._editorLoading { padding: 12px; color: ${t('fontColorThree', '#888')}; }

	/* Variable picker */
	${PREFIX} ._varPicker { position: relative; }
	${PREFIX} ._varButton {
		padding: 4px 10px;
		border: 1px solid ${border};
		border-radius: 4px;
		background: ${card};
		cursor: pointer;
		font-size: 12px;
	}
	${PREFIX} ._varDropdown {
		position: absolute;
		right: 0;
		top: 110%;
		z-index: 20;
		background: ${card};
		border: 1px solid ${border};
		border-radius: 4px;
		box-shadow: 0 4px 16px rgba(0,0,0,0.12);
		max-height: 260px;
		overflow: auto;
		min-width: 220px;
	}
	${PREFIX} ._varItem {
		display: flex;
		justify-content: space-between;
		gap: 10px;
		width: 100%;
		text-align: left;
		padding: 6px 10px;
		border: none;
		background: transparent;
		cursor: pointer;
		font-size: 12px;
	}
	${PREFIX} ._varItem:hover { background: ${wash}; }
	${PREFIX} ._varPath { font-family: monospace; }
	${PREFIX} ._varType { color: ${t('fontColorThree', '#999')}; font-size: 11px; }
	${PREFIX} ._varEmpty { padding: 10px; font-size: 12px; color: ${t('fontColorThree', '#888')}; max-width: 240px; }

	/* Preview */
	${PREFIX} ._previewPane {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
	}
	${PREFIX} ._previewToolbar {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 6px 10px;
		border-bottom: 1px solid ${border};
	}
	${PREFIX} ._previewTitle { font-size: 12px; font-weight: 600; color: ${t('fontColorTwo', '#666')}; }
	${PREFIX} ._refreshButton {
		padding: 4px 10px;
		border: 1px solid ${border};
		border-radius: 4px;
		background: ${card};
		cursor: pointer;
		font-size: 12px;
	}
	${PREFIX} ._previewLoading { font-size: 12px; color: ${selection}; }
	${PREFIX} ._previewError {
		padding: 8px 10px;
		background: ${t('errorWashColor', '#fef2f2')};
		color: ${danger};
		font-size: 12px;
		white-space: pre-wrap;
	}
	${PREFIX} ._deviceSizes { display: flex; align-items: center; gap: 6px; }
	${PREFIX} ._deviceSizes ._icon {
		padding: 4px; border-radius: 4px; height: 28px;
		display: flex; align-items: center; cursor: pointer;
		background: transparent; border: none;
	}
	${PREFIX} ._deviceSizes ._icon._selected,
	${PREFIX} ._deviceSizes ._icon:hover { background-color: ${t('surfaceColorTwo', '#f0f0f0')}; }

	${PREFIX} ._previewBody { flex: 1; min-height: 0; overflow: auto; background: ${t('surfaceColorTwo', '#f5f5f5')}; display: flex; justify-content: center; }
	${PREFIX} ._pdfPreview { width: 100%; height: 100%; border: none; }
	${PREFIX} ._htmlPreviewWrap { display: flex; flex-direction: column; width: 100%; }
	${PREFIX} ._previewSubject { padding: 8px 12px; background: ${card}; border-bottom: 1px solid ${border}; font-size: 13px; }
	${PREFIX} ._previewSubject span { color: ${t('fontColorThree', '#999')}; margin-right: 6px; }
	/* An email renders on a white client window whatever the editor's theme; on the theme's
	   card a dark theme hid every part the email leaves transparent. */
	${PREFIX} ._htmlPreview { border: none; background: #fff; flex: 1; width: 100%; height: 100%; }
	${PREFIX} ._htmlPreview.DESKTOP { }
	${PREFIX} ._htmlPreviewWrap.TABLET { max-width: 768px; }
	${PREFIX} ._htmlPreviewWrap.MOBILE { max-width: 400px; }
	${PREFIX} ._textPreview {
		padding: 16px; margin: 16px; background: ${card}; border-radius: 8px;
		white-space: pre-wrap; font-size: 14px; align-self: flex-start; max-width: 400px;
		box-shadow: 0 1px 4px rgba(0,0,0,0.1);
	}
	${PREFIX} ._previewPlaceholder { padding: 20px; color: ${t('fontColorThree', '#999')}; font-size: 13px; }

	/* Right panels */
	${PREFIX} ._panelHint { font-size: 12px; color: ${muted}; margin-bottom: 10px; }
	${PREFIX} ._jsonEditor { height: 320px; border: 1px solid ${border}; border-radius: 4px; }
	${PREFIX} ._sampleDataError { color: ${danger}; font-size: 12px; margin-top: 6px; }
	${PREFIX} ._settingsPanel { display: flex; flex-direction: column; gap: 12px; }
	${PREFIX} ._field { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: ${t('fontColorTwo', '#666')}; }
	${PREFIX} ._field input {
		padding: 6px 8px; border: 1px solid ${border}; border-radius: 4px; font-size: 13px;
	}
	${PREFIX} ._pdfHint { font-size: 12px; color: ${t('fontColorTwo', '#666')}; }
	${PREFIX} ._pdfHint pre {
		background: ${t('surfaceColorTwo', '#f5f5f5')}; padding: 10px; border-radius: 4px; overflow: auto; font-size: 11px;
	}

	/* Mode toggle (Visual / Code) */
	${PREFIX} ._modeToggle { display: flex; gap: 4px; padding: 8px 8px 0 8px; }
	${PREFIX} ._modeBtn {
		padding: 5px 12px; border: 1px solid ${border}; border-radius: 4px;
		background: ${card}; cursor: pointer; font-size: 12px;
	}
	${PREFIX} ._modeBtn._active { background: ${ink}; color: ${card}; border-color: ${ink}; }

	/* Visual area = palette + canvas */
	${PREFIX} ._visualArea { display: flex; flex: 1; min-height: 0; }
	${PREFIX} ._blockPalette {
		flex: 0 0 132px; border-right: 1px solid ${border}; padding: 8px; overflow: auto;
		display: flex; flex-direction: column; gap: 6px; background: ${t('surfaceColorOne', '#fafafa')};
	}
	${PREFIX} ._paletteTitle { font-size: 11px; text-transform: uppercase; color: ${t('fontColorThree', '#999')}; margin-bottom: 2px; }
	${PREFIX} ._paletteItem {
		display: flex; align-items: center; gap: 8px; padding: 8px; border: 1px solid ${border};
		border-radius: 4px; background: ${card}; cursor: grab; font-size: 12px;
	}
	${PREFIX} ._paletteItem:hover { border-color: ${selection}; }
	${PREFIX} ._paletteItem i { width: 14px; text-align: center; color: ${t('fontColorTwo', '#555')}; }

	${PREFIX} ._blockCanvas { flex: 1; min-height: 0; overflow: auto; padding: 8px 16px; background: ${t('surfaceColorTwo', '#f5f5f5')}; }
	${PREFIX} ._canvasEmpty, ${PREFIX} ._canvasNote {
		padding: 20px; text-align: center; color: ${t('fontColorThree', '#888')}; font-size: 13px;
		border: 1px dashed ${border}; border-radius: 6px; background: ${card}; margin: 8px 0;
	}
	${PREFIX} ._canvasNote p { margin: 0 0 12px; line-height: 1.6; }
	${PREFIX} ._importBtn {
		display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; cursor: pointer;
		border: 1px solid ${accent}; border-radius: 6px; background: ${wash}; color: ${accentText};
		font-size: 13px; font-weight: 600;
	}
	${PREFIX} ._importBtn:hover { background: ${accent}; color: ${card}; }
	${PREFIX} ._dropGap { height: 8px; border-radius: 4px; transition: all 0.08s; }
	${PREFIX} ._dropGap._active { height: 20px; background: ${wash}; border: 1px dashed ${selection}; }
	${PREFIX} ._blockRow {
		position: relative; background: ${card}; border: 1px solid transparent; border-radius: 6px;
		cursor: pointer; overflow: hidden;
	}
	${PREFIX} ._blockRow:hover { border-color: ${border}; }
	${PREFIX} ._blockRow._selected { border-color: ${selection}; box-shadow: 0 0 0 1px ${selection}; }
	${PREFIX} ._blockPreview { pointer-events: none; }
	${PREFIX} ._blockActions {
		position: absolute; top: 4px; right: 4px; display: none; gap: 4px; align-items: center;
		background: ${card}; border: 1px solid ${border}; border-radius: 4px; padding: 2px 4px;
	}
	${PREFIX} ._blockRow:hover ._blockActions, ${PREFIX} ._blockRow._selected ._blockActions { display: flex; }
	${PREFIX} ._blockActions button {
		border: none; background: transparent; cursor: pointer; color: ${t('fontColorTwo', '#555')}; padding: 2px 4px; font-size: 12px;
	}
	${PREFIX} ._blockActions button:hover { color: ${ink}; }
	${PREFIX} ._blockTypeTag { font-size: 10px; color: ${t('fontColorThree', '#999')}; text-transform: uppercase; margin-right: 4px; }

	/* Block properties panel */
	${PREFIX} ._blockProps { display: flex; flex-direction: column; gap: 10px; }
	${PREFIX} ._bpHeader {
		display: flex; align-items: center; justify-content: space-between;
		font-weight: 600; font-size: 13px; padding-bottom: 6px; border-bottom: 1px solid ${border};
	}
	${PREFIX} ._bpClose { border: none; background: transparent; cursor: pointer; color: ${t('fontColorThree', '#888')}; }
	${PREFIX} ._bpField { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: ${t('fontColorTwo', '#666')}; }
	${PREFIX} ._bpInput { padding: 6px 8px; border: 1px solid ${border}; border-radius: 4px; font-size: 13px; }
	${PREFIX} ._bpTextarea { min-height: 70px; resize: vertical; font-family: inherit; }
	${PREFIX} ._bpColor { display: flex; gap: 6px; align-items: center; }
	${PREFIX} ._bpColor input[type=color] { width: 34px; height: 30px; padding: 0; border: 1px solid ${border}; border-radius: 4px; }
	${PREFIX} ._bpColor ._bpInput { flex: 1; }
	${PREFIX} ._bpCode { border: 1px solid ${border}; border-radius: 4px; overflow: hidden; }
	${PREFIX} ._bpVars { margin-top: 4px; }
	${PREFIX} ._bpVarsToggle { border: none; background: transparent; color: ${selection}; cursor: pointer; font-size: 12px; padding: 4px 0; }
	${PREFIX} ._bpVarsList { display: flex; flex-direction: column; gap: 2px; max-height: 180px; overflow: auto; }
	${PREFIX} ._bpVarItem {
		display: flex; justify-content: space-between; gap: 8px; border: none; background: transparent;
		cursor: pointer; padding: 4px 6px; font-size: 12px; text-align: left; border-radius: 3px;
	}
	${PREFIX} ._bpVarItem:hover { background: ${wash}; }
	${PREFIX} ._bpCodeArea {
		min-height: 160px; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
		font-size: 12px; line-height: 1.5;
	}
	${PREFIX} ._bpSection {
		display: flex; flex-direction: column; gap: 10px;
		border-top: 1px solid ${border}; padding-top: 10px; margin-top: 2px;
	}
	${PREFIX} ._bpSectionToggle {
		border: none; background: transparent; color: ${t('fontColorTwo', '#374151')}; cursor: pointer;
		font-size: 12px; font-weight: 600; padding: 2px 0; text-align: left;
	}
	${PREFIX} ._rootSettings ._panelHint { margin-bottom: 2px; }

	/* AI tab: prompt-driven authoring */
	${PREFIX} ._aiPanel { display: flex; flex-direction: column; gap: 10px; height: 100%; box-sizing: border-box; padding: 12px 14px; }
	${PREFIX} ._aiHistory { flex: 1; overflow: auto; display: flex; flex-direction: column; gap: 8px; padding: 2px; min-height: 120px; }
	${PREFIX} ._aiExamples { display: flex; flex-direction: column; gap: 6px; }
	${PREFIX} ._aiExample {
		text-align: left; border: 1px dashed ${border}; background: ${panel}; border-radius: 8px;
		padding: 8px 10px; font-size: 12px; color: ${t('fontColorTwo', '#555')}; cursor: pointer;
	}
	${PREFIX} ._aiExample:hover { background: ${wash}; border-color: ${selection}; }
	${PREFIX} ._aiMsg {
		padding: 8px 12px; border-radius: 10px; font-size: 13px; line-height: 1.5;
		max-width: 92%; white-space: pre-wrap; word-break: break-word;
	}
	${PREFIX} ._aiMsg._user { align-self: flex-end; background: ${wash}; color: ${accentText}; }
	${PREFIX} ._aiMsg._assistant { align-self: flex-start; background: ${t('surfaceColorTwo', '#f3f4f6')}; color: ${t('fontColorTwo', '#374151')}; }
	${PREFIX} ._aiMsg._aiLoading { color: ${t('fontColorThree', '#6b7280')}; font-style: italic; }
	${PREFIX} ._aiComposer { display: flex; flex-direction: column; gap: 8px; border-top: 1px solid ${line}; padding-top: 10px; }
	${PREFIX} ._aiInput {
		min-height: 72px; resize: vertical; border: 1px solid ${border}; border-radius: 8px;
		padding: 8px 10px; font-family: inherit; font-size: 13px;
	}
	${PREFIX} ._aiSend {
		align-self: flex-end; display: inline-flex; align-items: center; gap: 6px;
		background: ${accent}; color: ${card}; border: none; border-radius: 8px;
		padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer;
	}
	${PREFIX} ._aiSend:disabled { opacity: 0.5; cursor: default; }

	/* Monaco loads its editor CSS from a CDN; before/if that CSS applies, its internal input
	   textareas (.inputarea / .ime-text-area) fall back to the browser default border + resize
	   handle and show as stray boxes over the code. Force them inert so that never happens,
	   regardless of CSS load timing. These elements only need to receive keystrokes. */
	${PREFIX} .monaco-editor textarea,
	${PREFIX} .monaco-editor .inputarea,
	${PREFIX} .monaco-editor .ime-text-area {
		border: 0 !important;
		outline: 0 !important;
		resize: none !important;
		box-shadow: none !important;
		background: transparent !important;
		padding: 0 !important;
		margin: 0 !important;
	}

	/* ---- Look & feel refresh ---- */
	${PREFIX} ._teToolbar { background: ${panel}; }
	${PREFIX} ._teType, ${PREFIX} ._teTypeChip {
		display: inline-flex; align-items: center; gap: 6px;
		padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 600;
		background: ${wash}; color: ${accentText}; border: 1px solid ${line};
	}
	${PREFIX} ._teTypeChip i { font-size: 12px; }

	/* Side panels */
	${PREFIX} ._panel { display: flex; flex-direction: column; gap: 12px; height: 100%; }
	${PREFIX} ._panelHint { font-size: 12px; color: ${t('fontColorThree', '#6b7280')}; line-height: 1.5; margin: 0; }
	${PREFIX} ._panelError { color: ${danger}; font-size: 12px; background: ${t('errorWashColor', '#fef2f2')}; padding: 6px 8px; border-radius: 4px; }
	${PREFIX} ._muted { color: ${faint}; font-size: 12px; }

	/* Segmented control */
	${PREFIX} ._segmented { display: inline-flex; background: ${t('surfaceColorTwo', '#f3f4f6')}; border-radius: 8px; padding: 3px; gap: 2px; }
	${PREFIX} ._segmented button {
		border: none; background: transparent; padding: 6px 14px; border-radius: 6px;
		font-size: 12px; cursor: pointer; color: ${t('fontColorThree', '#6b7280')}; font-weight: 500;
	}
	${PREFIX} ._segmented button._on { background: ${card}; color: ${ink}; box-shadow: 0 1px 2px rgba(0,0,0,0.08); }

	${PREFIX} ._modeBody { display: flex; flex-direction: column; gap: 8px; flex: 1; min-height: 0; }

	/* Fields */
	${PREFIX} ._fld {
		width: 100%; padding: 8px 10px; border: 1px solid ${border}; border-radius: 6px;
		font-size: 13px; background: ${card}; box-sizing: border-box;
	}
	${PREFIX} ._fld:focus { outline: none; border-color: ${accent}; box-shadow: 0 0 0 3px ${wash}; }
	${PREFIX} ._kvRow { display: flex; gap: 6px; align-items: center; }
	${PREFIX} ._kvRow ._fld { flex: 1; }
	${PREFIX} ._fldType { flex: 0 0 110px; }

	/* Buttons */
	${PREFIX} ._addBtn {
		align-self: flex-start; display: inline-flex; align-items: center; gap: 6px;
		padding: 6px 12px; border: 1px dashed ${border}; border-radius: 6px; background: ${card};
		cursor: pointer; font-size: 12px; color: ${muted};
	}
	${PREFIX} ._addBtn:hover { border-color: ${accent}; color: ${accentText}; }
	${PREFIX} ._iconBtn { border: none; background: transparent; cursor: pointer; color: ${faint}; padding: 6px; }
	${PREFIX} ._iconBtn:hover { color: ${danger}; }
	${PREFIX} ._ghostBtn {
		display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border: 1px solid ${border};
		border-radius: 5px; background: ${card}; cursor: pointer; font-size: 12px; color: ${muted};
	}
	${PREFIX} ._ghostBtn:hover { border-color: ${accent}; color: ${accentText}; }

	/* Field summary + nested note */
	${PREFIX} ._fieldSummary { display: flex; flex-direction: column; gap: 4px; }
	${PREFIX} ._fieldRow { display: flex; justify-content: space-between; padding: 5px 8px; background: ${t('surfaceColorOne', '#f9fafb')}; border-radius: 4px; font-size: 12px; }
	${PREFIX} ._fieldRow code { font-family: monospace; color: ${ink}; }
	${PREFIX} ._nestedNote { font-style: italic; }

	/* JSON editor box */
	${PREFIX} ._jsonBox { flex: 1; min-height: 240px; border: 1px solid ${border}; border-radius: 6px; overflow: hidden; }

	/* Part toolbar actions */
	${PREFIX} ._partToolbarActions { display: flex; align-items: center; gap: 8px; }

	/* Softer preview divider + editable subject line */
	${PREFIX} ._previewToolbar { border-bottom: 1px solid ${line}; }
	${PREFIX} ._subjectLine {
		display: flex; align-items: center; gap: 10px; padding: 9px 14px;
		border-bottom: 1px solid ${line}; background: ${card};
	}
	${PREFIX} ._subjectLine span { font-size: 11px; text-transform: uppercase; color: ${faint}; letter-spacing: 0.04em; }
	${PREFIX} ._subjectLine input {
		flex: 1; border: none; outline: none; font-size: 14px; font-weight: 600; color: ${ink}; background: transparent;
	}

	/* Embedded schema builder (Custom variables) */
	${PREFIX} ._schemaBuilderBox { overflow: auto; }
	${PREFIX} ._schemaBuilderBox .comp.compSchemaBuilder { padding: 0; }
	` + processStyleDefinition(PREFIX, styleProperties, styleDefaults, theme);

	return <style id="TemplateEditorCss">{css}</style>;
}
